import React, { useState, useRef, useEffect } from 'react';
import { Search, Printer, ChevronRight, ChevronLeft, LayoutPanelLeft } from 'lucide-react';
import { printContent } from '../utils/print';

export interface Column<T = any> {
  header: string;
  accessor: keyof T | string;
  render?: (val: any, row: T) => React.ReactNode;
}

interface ReportTableProps {
  title: string;
  columns: Column[];
  data: any[];
  searchPlaceholder?: string;
  onFilterChange?: (filterValue: string) => void;
  customFilters?: React.ReactNode;
  description?: string;
  printSummaryHtml?: string;
}

const PAGE_SIZE = 15;

const getRenderedText = (node: any): string => {
  if (node === null || node === undefined) return '';
  if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(getRenderedText).join('');
  }
  if (typeof node === 'object' && node.props && node.props.children !== undefined) {
    return getRenderedText(node.props.children);
  }
  return '';
};

const ReportTable: React.FC<ReportTableProps> = ({ title, columns, data, searchPlaceholder, customFilters, description, printSummaryHtml }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, data]);

  const filteredData = data.filter(row => {
    if (!searchTerm) return true;
    return Object.values(row).some(val => 
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedData = filteredData.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePrint = () => {
    // Print logic uses filteredData (ALL items), not just paginated ones
    const companyInfoStr = localStorage.getItem('companyInfo');
    const companyInfo = companyInfoStr ? JSON.parse(companyInfoStr) : null;
    const logoHtml = companyInfo?.logo_url ? `<img src="${companyInfo.logo_url}" alt="${companyInfo.name_en || 'Logo'}" style="max-height: 60px; max-width: 100%; object-fit: contain;" />` : '';
    const companyName = companyInfo?.name_ar || 'نظام إدارة العقارات';

    const printHtml = `
      <div dir="rtl" style="font-family: Arial, sans-serif; padding: 20px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 25px; border-bottom: 3px solid #0f172a; padding-bottom: 15px; font-size: 11px; color: #334155; line-height: 1.6;">
          <!-- اليمين: بيانات المؤسسة بالعربية -->
          <div style="text-align: right; flex: 1;">
            <h1 style="margin: 0 0 5px 0; color: #0f172a; font-size: 18px; font-weight: 900;">${companyName}</h1>
            ${companyInfo?.address_ar ? `<div style="margin-bottom: 2px;">العنوان: ${companyInfo.address_ar}</div>` : ''}
            ${companyInfo?.phone ? `<div style="margin-bottom: 2px;">الهاتف: ${companyInfo.phone}</div>` : ''}
            ${companyInfo?.fax ? `<div style="margin-bottom: 2px;">الفاكس: ${companyInfo.fax}</div>` : ''}
            ${companyInfo?.email ? `<div style="margin-bottom: 2px;">البريد: ${companyInfo.email}</div>` : ''}
            ${companyInfo?.cr_number ? `<div style="margin-bottom: 2px;">السجل التجاري: ${companyInfo.cr_number}</div>` : ''}
          </div>
          
          <!-- المنتصف: الشعار -->
          <div style="text-align: center; flex: 0 0 120px; padding: 0 10px;">
            ${logoHtml}
          </div>
          
          <!-- اليسار: بيانات المؤسسة بالإنجليزية وتفاصيل التقرير -->
          <div style="text-align: left; flex: 1; dir: ltr;" dir="ltr">
            <h1 style="margin: 0 0 5px 0; color: #0f172a; font-size: 16px; font-weight: 900;">${companyInfo?.name_en || 'Property Management System'}</h1>
            ${companyInfo?.address_en ? `<div style="margin-bottom: 2px;">Address: ${companyInfo.address_en}</div>` : ''}
            <div style="margin-top: 8px; font-weight: bold; color: #0f172a; font-size: 13px;">${title}</div>
            <div style="margin-top: 2px; color: #64748b; font-size: 10px;">تاريخ الطباعة: ${new Date().toLocaleDateString('ar-KW')} ${new Date().toLocaleTimeString('ar-KW')}</div>
          </div>
        </div>
        <table style="width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px;">
          <thead>
            <tr style="background-color: #f8fafc; border-bottom: 2px solid #cbd5e1;">
              ${columns.map(col => `<th style="padding: 10px; text-align: right; border-bottom: 1px solid #e2e8f0;">${col.header}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${filteredData.map(row => `
              <tr style="border-bottom: 1px solid #e2e8f0;">
                ${columns.map(col => {
                  let val = typeof col.accessor === 'string' ? row[col.accessor] : row[col.accessor as keyof typeof row];
                  if (col.render) {
                    try {
                      const rendered = col.render(val, row);
                      val = getRenderedText(rendered);
                    } catch (e) {
                      // fallback to raw val if render fails
                    }
                  }
                  if (val === undefined || val === null) val = '';
                  return `<td style="padding: 10px; text-align: right;">${val}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
          <tfoot>
            ${printSummaryHtml ? `
              <tr style="font-weight: bold; background-color: #f8fafc; border-top: 2px solid #cbd5e1;">
                <td colspan="${columns.length}" style="padding: 12px; text-align: right; border-bottom: 2px solid #cbd5e1;">
                  ${printSummaryHtml}
                </td>
              </tr>
            ` : ''}
             <tr><td colspan="${columns.length}" style="padding-top: 20px; text-align: left; font-size: 11px; color: #94a3b8; border-top: 1px solid #eee;">تم إنشاء هذا التقرير آلياً - إجمالي السجلات: ${filteredData.length}</td></tr>
          </tfoot>
        </table>
      </div>
    `;

    printContent(title, printHtml);
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full animate-in fade-in zoom-in duration-500 scale-95 md:scale-100">
      <div className="p-6 border-b border-slate-100 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-slate-50/50">
        <div className="flex-1 max-w-full xl:max-w-[50%]">
           <div className="flex items-center gap-2 mb-1">
              <LayoutPanelLeft className="w-5 h-5 text-fly-600" />
              <h3 className="text-xl font-bold text-slate-800">{title}</h3>
           </div>
           <p className="text-sm text-slate-500 leading-relaxed">{description || 'استعراض وتصفية دقيقة لكافة البيانات'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto xl:justify-end shrink-0">
          {customFilters}
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input 
              type="text" 
              placeholder={searchPlaceholder || 'بحث سريع...'} 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full sm:w-56 pr-10 pl-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-fly-500 focus:ring-4 focus:ring-fly-500/10 transition-all font-medium placeholder-slate-400 shadow-sm"
            />
          </div>
          <button 
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 px-6 py-2 bg-slate-900 text-white rounded-xl text-sm font-bold hover:bg-slate-800 transition-all active:scale-95 shadow-xl shadow-slate-900/20 shrink-0"
          >
            <Printer className="w-4 h-4" />
            <span>طباعة / PDF</span>
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto custom-scrollbar">
        <table className="w-full text-sm text-right">
          <thead className="bg-slate-50 font-bold text-slate-700 whitespace-nowrap sticky top-0 z-10 border-b border-slate-200">
            <tr>
              {columns.map((col, i) => (
                <th key={i} className="p-4 uppercase tracking-wider text-[10px] text-slate-500 text-right">{col.header}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedData.length > 0 ? (
                paginatedData.map((row, i) => (
                <tr key={i} className="hover:bg-slate-50/80 transition-colors group">
                    {columns.map((col, j) => (
                    <td key={j} className="p-4 whitespace-nowrap text-slate-700 font-medium group-hover:text-fly-700">
                        {col.render ? col.render(row[col.accessor as keyof typeof row], row) : (row[col.accessor as keyof typeof row] || '-')}
                    </td>
                    ))}
                </tr>
                ))
            ) : (
                <tr>
                    <td colSpan={columns.length} className="p-16 text-center text-slate-400 font-bold bg-white">
                       <div className="flex flex-col items-center gap-2">
                          <Search className="w-10 h-10 text-slate-100" />
                          <span>عذراً، لم يتم العثور على أي نتائج مطابقة</span>
                       </div>
                    </td>
                </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Footer with Pagination */}
      <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-xs font-bold text-slate-500 bg-white border border-slate-200 px-4 py-2 rounded-lg shadow-sm">
             عرض <span className="text-fly-600 font-black">{paginatedData.length}</span> سجل من أصل <span className="text-slate-800 font-black">{filteredData.length}</span>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
                <button 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
                
                <div className="flex items-center gap-1 shadow-sm rounded-lg border border-slate-200 bg-white p-1">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                        // Logic to show pages around current
                        let pageNum = i + 1;
                        if (totalPages > 5 && currentPage > 3) {
                            pageNum = currentPage - 2 + i;
                            if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                        }
                        
                        return (
                            <button
                                key={pageNum}
                                onClick={() => setCurrentPage(pageNum)}
                                className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-all ${
                                    currentPage === pageNum 
                                    ? 'bg-fly-600 text-white shadow-lg shadow-fly-600/30' 
                                    : 'text-slate-500 hover:bg-slate-100'
                                }`}
                            >
                                {pageNum}
                            </button>
                        );
                    })}
                </div>

                <button 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default ReportTable;
